'use client';

import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useFoundry } from '@/lib/foundry/context';
import { isCanvasGenerating, getCanvasError } from '@/lib/foundry/usePlanPolling';
import type { LeanCanvas, UserContext } from '@/lib/foundry/types';

// Lazy load chart components to avoid SSR issues with Recharts
const DefensibilityGauge = dynamic(
  () => import('@/components/foundry/charts/DefensibilityGauge'),
  { ssr: false, loading: () => <div className="h-32 bg-background/40 animate-pulse rounded-lg" /> }
);
const CanvasCompletionChart = dynamic(
  () => import('@/components/foundry/charts/CanvasCompletionChart'),
  { ssr: false, loading: () => <div className="h-32 bg-background/40 animate-pulse rounded-lg" /> }
);

interface CanvasBlockProps {
  title: string;
  value: unknown;
  onChange: (value: unknown) => void;
  placeholder?: string;
  multiline?: boolean;
}

function CanvasBlock({ title, value, onChange, placeholder, multiline = true }: CanvasBlockProps) {
  // Convert value to displayable string
  const displayValue = typeof value === 'object' && value !== null
    ? JSON.stringify(value, null, 2)
    : String(value || '');

  return (
    <div className="p-4 bg-background/60 border border-border-muted rounded-lg h-full flex flex-col">
      <h4 className="text-sm font-semibold text-accent mb-2">{title}</h4>
      {multiline ? (
        <textarea
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 w-full bg-transparent text-foreground-secondary text-sm resize-none focus:outline-none placeholder-foreground/30 min-h-[100px]"
        />
      ) : (
        <input
          type="text"
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-transparent text-foreground-secondary text-sm focus:outline-none placeholder-foreground/30"
        />
      )}
    </div>
  );
}

export function LeanCanvasEditor() {
  const { state, actions } = useFoundry();
  const plan = state.currentPlan;

  const [businessIdea, setBusinessIdea] = useState('');
  const [userContext, setUserContext] = useState<Partial<UserContext>>({});
  const [showContextForm, setShowContextForm] = useState(false);

  // Check if canvas has content
  const hasCanvas = plan?.lean_canvas?.problem !== null && plan?.lean_canvas?.problem !== undefined;

  // Check async canvas generation status
  const canvasGenerating = isCanvasGenerating(plan);
  const canvasError = getCanvasError(plan);

   
  useEffect(() => {
    if (plan?.business_idea) {
      setBusinessIdea(plan.business_idea);
    }
    if (plan?.user_context) {
      setUserContext(plan.user_context);
    }
    // Show form only if no canvas content and not generating
    setShowContextForm(!hasCanvas && !canvasGenerating);
  }, [plan, hasCanvas, canvasGenerating]);
   

  const handleGenerateCanvas = async () => {
    if (!businessIdea.trim()) {
      alert('Please enter your business idea');
      return;
    }
    await actions.generateCanvas(businessIdea.trim(), userContext);
    // Form will hide automatically when canvas is populated via polling
  };

  if (!plan) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Canvas Generating State */}
      {canvasGenerating && (
        <div className="p-6 bg-background/40 border border-accent/40 rounded-lg animate-pulse">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            <div>
              <h3 className="text-lg font-semibold text-foreground">Generating Lean Canvas</h3>
              <p className="text-foreground-tertiary text-sm mt-1">
                AI is analyzing your business idea and creating a comprehensive canvas...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Canvas Error State */}
      {canvasError && !canvasGenerating && (
        <div className="p-4 bg-red-500/20 border border-red-500/40 rounded-lg">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-red-300 font-medium">Canvas generation failed</p>
              <p className="text-red-300/70 text-sm mt-1">{canvasError}</p>
            </div>
            <button
              onClick={handleGenerateCanvas}
              className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-sm"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Business Idea Input */}
      {(showContextForm || !hasCanvas) && !canvasGenerating && (
        <div className="p-6 bg-background/40 border border-border-muted rounded-lg space-y-4">
          <h3 className="text-lg font-semibold text-foreground">
            {hasCanvas ? 'Regenerate Canvas' : 'Describe Your Business Idea'}
          </h3>

          <div>
            <label className="block text-sm text-foreground-tertiary mb-2">
              Business Idea *
            </label>
            <textarea
              value={businessIdea}
              onChange={(e) => setBusinessIdea(e.target.value)}
              placeholder="Describe your business idea in a few sentences. What problem does it solve? Who is it for?"
              className="w-full h-32 px-4 py-3 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent resize-none"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-foreground-tertiary mb-2">
                Available Resources
              </label>
              <input
                type="text"
                value={userContext.available_resources || ''}
                onChange={(e) =>
                  setUserContext({ ...userContext, available_resources: e.target.value })
                }
                placeholder="e.g., $50k savings, 2 co-founders, weekends only"
                className="w-full px-4 py-2 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent"
              />
            </div>

            <div>
              <label className="block text-sm text-foreground-tertiary mb-2">
                Team Background
              </label>
              <input
                type="text"
                value={userContext.team_background || ''}
                onChange={(e) =>
                  setUserContext({ ...userContext, team_background: e.target.value })
                }
                placeholder="e.g., 10 years in fintech, former product manager"
                className="w-full px-4 py-2 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent"
              />
            </div>

            <div>
              <label className="block text-sm text-foreground-tertiary mb-2">
                Existing Business
              </label>
              <input
                type="text"
                value={userContext.existing_business || ''}
                onChange={(e) =>
                  setUserContext({ ...userContext, existing_business: e.target.value })
                }
                placeholder="e.g., Running a consulting agency with 5 clients"
                className="w-full px-4 py-2 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent"
              />
            </div>

            <div>
              <label className="block text-sm text-foreground-tertiary mb-2">
                Funding Status
              </label>
              <input
                type="text"
                value={userContext.funding_status || ''}
                onChange={(e) =>
                  setUserContext({ ...userContext, funding_status: e.target.value })
                }
                placeholder="e.g., Bootstrapped, seeking seed funding"
                className="w-full px-4 py-2 bg-background/60 border border-border rounded-lg text-foreground placeholder-foreground-muted focus:outline-none focus:border-accent"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleGenerateCanvas}
              disabled={canvasGenerating || !businessIdea.trim()}
              className="px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {hasCanvas ? 'Regenerate Canvas' : 'Generate Lean Canvas'}
            </button>

            {hasCanvas && (
              <button
                onClick={() => setShowContextForm(false)}
                className="px-6 py-3 border border-border text-foreground rounded-lg hover:border-border/40 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>

          {state.aiError && (
            <div className="p-4 bg-red-500/20 border border-red-500/40 rounded-lg text-red-300">
              {state.aiError}
            </div>
          )}
        </div>
      )}

      {/* Canvas Grid */}
      {hasCanvas && !showContextForm && (
        <>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Lean Canvas</h3>
            <button
              onClick={() => setShowContextForm(true)}
              disabled={canvasGenerating}
              className="text-sm text-accent hover:text-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {canvasGenerating ? 'Generating...' : 'Edit Business Idea'}
            </button>
          </div>

          <div className="grid grid-cols-5 gap-3">
            {/* Row 1 */}
            <div className="col-span-1 row-span-2">
              <CanvasBlock
                title="Problem"
                value={formatCanvasValue(plan.lean_canvas.problem)}
                onChange={() => {}}
                placeholder="Top 3 problems"
              />
            </div>

            <div className="col-span-1">
              <CanvasBlock
                title="Solution"
                value={formatCanvasValue(plan.lean_canvas.solution)}
                onChange={() => {}}
                placeholder="Top 3 features"
              />
            </div>

            <div className="col-span-1 row-span-2">
              <CanvasBlock
                title="Unique Value Proposition"
                value={formatCanvasValue(plan.lean_canvas.unique_value_proposition)}
                onChange={() => {}}
                placeholder="Clear, compelling message"
              />
            </div>

            <div className="col-span-1">
              <CanvasBlock
                title="Unfair Advantage"
                value={formatCanvasValue(plan.lean_canvas.unfair_advantage)}
                onChange={() => {}}
                placeholder="Can't be easily copied"
              />
            </div>

            <div className="col-span-1 row-span-2">
              <CanvasBlock
                title="Customer Segments"
                value={formatCanvasValue(plan.lean_canvas.customer_segments)}
                onChange={() => {}}
                placeholder="Target customers"
              />
            </div>

            {/* Row 2 */}
            <div className="col-span-1">
              <CanvasBlock
                title="Key Metrics"
                value={formatCanvasValue(plan.lean_canvas.key_metrics)}
                onChange={() => {}}
                placeholder="Key activities you measure"
              />
            </div>

            <div className="col-span-1">
              <CanvasBlock
                title="Channels"
                value={formatCanvasValue(plan.lean_canvas.channels)}
                onChange={() => {}}
                placeholder="Path to customers"
              />
            </div>

            {/* Row 3 */}
            <div className="col-span-2">
              <CanvasBlock
                title="Cost Structure"
                value={formatCanvasValue(plan.lean_canvas.cost_structure)}
                onChange={() => {}}
                placeholder="List your fixed and variable costs"
              />
            </div>

            <div className="col-span-3">
              <CanvasBlock
                title="Revenue Streams"
                value={formatCanvasValue(plan.lean_canvas.revenue_streams)}
                onChange={() => {}}
                placeholder="Revenue model, pricing"
              />
            </div>
          </div>

          {/* Canvas Metrics */}
          <div className="mt-6 pt-6 border-t border-border-muted">
            <h4 className="text-sm font-semibold text-foreground-tertiary mb-4">Canvas Metrics</h4>
            <div className="grid md:grid-cols-2 gap-4">
              {/* Canvas Completion */}
              <CanvasCompletionChart canvas={plan.lean_canvas} />

              {/* Defensibility Gauge (only show if unfair_advantage has a score) */}
              {plan.lean_canvas.unfair_advantage?.defensibility_score !== undefined && (
                <DefensibilityGauge
                  score={plan.lean_canvas.unfair_advantage.defensibility_score}
                  moatType={plan.lean_canvas.unfair_advantage.moat_type}
                />
              )}
            </div>

            {/* Revenue Targets Summary */}
            {plan.lean_canvas.revenue_streams?.revenue_targets && (
              <div className="mt-4 p-4 bg-background/40 border border-border-muted rounded-lg">
                <h4 className="text-sm font-semibold text-foreground mb-3">Revenue Targets</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-xs text-foreground-tertiary">Year 1</div>
                    <div className="text-lg font-semibold text-accent">
                      ${(plan.lean_canvas.revenue_streams.revenue_targets.year1 / 1000).toFixed(0)}K
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-foreground-tertiary">Year 3</div>
                    <div className="text-lg font-semibold text-accent">
                      ${plan.lean_canvas.revenue_streams.revenue_targets.year3 >= 1000000
                        ? `${(plan.lean_canvas.revenue_streams.revenue_targets.year3 / 1000000).toFixed(1)}M`
                        : `${(plan.lean_canvas.revenue_streams.revenue_targets.year3 / 1000).toFixed(0)}K`
                      }
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs text-foreground-tertiary">Year 5</div>
                    <div className="text-lg font-semibold text-accent">
                      ${plan.lean_canvas.revenue_streams.revenue_targets.year5 >= 1000000
                        ? `${(plan.lean_canvas.revenue_streams.revenue_targets.year5 / 1000000).toFixed(1)}M`
                        : `${(plan.lean_canvas.revenue_streams.revenue_targets.year5 / 1000).toFixed(0)}K`
                      }
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function formatCanvasValue(value: unknown): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    // Format object for display
    const entries = Object.entries(value as Record<string, unknown>);
    return entries
      .map(([key, val]) => {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        if (Array.isArray(val)) {
          return `${label}:\n${val.map((v) => `  - ${v}`).join('\n')}`;
        }
        if (typeof val === 'object' && val !== null) {
          return `${label}: ${JSON.stringify(val)}`;
        }
        return `${label}: ${val}`;
      })
      .join('\n\n');
  }
  return String(value);
}
